import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import PostCard from '@/components/feature/PostCard';
import { useSeo } from '@/hooks/useSeo';
import {
  formatDate,
  formatViews,
  getCategoryBySlug,
  getPostBySlug,
  getRelatedPosts,
  toneClasses,
} from '@/utils/content';

export default function PostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug);
  const category = getCategoryBySlug(post?.categorySlug);
  const related = post ? getRelatedPosts(post, 3) : [];
  const tone = toneClasses[category?.tone ?? 'primary'];

  useSeo({
    title: post ? `${post.title} - 瓜田爆料` : '话题详情 - 瓜田爆料',
    description: post ? post.excerpt : '瓜田爆料话题详情页。',
    keywords: post ? [post.tags.join(','), category?.name ?? ''].filter(Boolean).join(',') : '瓜田爆料',
    canonicalPath: `/post/${post?.slug ?? ''}`,
    ogType: 'article',
    jsonLd: post
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.excerpt,
            articleSection: category?.name,
            keywords: post.tags.join(','),
            author: { '@type': 'Organization', name: post.author },
            publisher: {
              '@type': 'Organization',
              name: '瓜田爆料',
              url: 'https://yoyoermei.cn/',
            },
            datePublished: post.publishedAt,
            dateModified: post.publishedAt,
            inLanguage: 'zh-CN',
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://yoyoermei.cn/post/${post.slug}`,
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://yoyoermei.cn/' },
              {
                '@type': 'ListItem',
                position: 2,
                name: category?.name ?? '分类合集',
                item: `https://yoyoermei.cn/category/${category?.slug ?? ''}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: post.title,
                item: `https://yoyoermei.cn/post/${post.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!post) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-6 py-24 text-center">
          <i className="ri-file-unknow-line text-4xl text-primary-500"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
            这篇话题不存在
          </h1>
          <p className="mt-3 text-sm text-foreground-600">
            内容可能已调整或链接有误，可以回到首页继续浏览分类合集。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <article className="w-full">
        <header className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category?.name ?? '分类合集', to: `/category/${category?.slug ?? ''}` },
                { label: post.title },
              ]}
            />
            <div className="mt-6 max-w-4xl">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Link
                  to={`/category/${category?.slug ?? ''}`}
                  className={`rounded-full px-3 py-1 text-xs font-medium cursor-pointer ${tone.soft}`}
                >
                  {category?.name}
                </Link>
                {post.hot && (
                  <span className="rounded-full px-3 py-1 text-xs font-medium bg-primary-500 text-background-50">
                    热榜话题
                  </span>
                )}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold leading-snug text-foreground-950">
                {post.title}
              </h1>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                {post.excerpt}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs md:text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {post.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line"></i>
                  {formatDate(post.publishedAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  约 {post.readMinutes} 分钟
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {formatViews(post.views)} 次阅读
                </span>
              </div>
            </div>
          </div>
        </header>

        <div className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10 lg:gap-14">
            <div className="min-w-0">
              <div className="w-full h-56 md:h-[400px] rounded-lg overflow-hidden border border-background-200 bg-background-100">
                <img
                  src={post.cover}
                  alt={post.coverAlt}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="mt-8 md:mt-10 space-y-9 max-w-3xl">
                {post.sections.map((section, index) => (
                  <section key={section.heading} id={`section-${index + 1}`} className="scroll-mt-24">
                    <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug">
                      {section.heading}
                    </h2>
                    <div className="mt-4 space-y-4">
                      {section.paragraphs.map((paragraph) => (
                        <p
                          key={paragraph.slice(0, 16)}
                          className="text-[15px] md:text-base leading-[1.9] text-foreground-700"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </section>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 mr-1">话题标签：</span>
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 text-secondary-900 px-3 py-1 text-xs"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <aside className="flex flex-col gap-6">
              <nav
                aria-label="内容目录"
                className="rounded-lg border border-background-200 bg-background-50 p-6 lg:sticky lg:top-24"
              >
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">
                  内容目录
                </h2>
                <ol className="space-y-2.5">
                  {post.sections.map((section, index) => (
                    <li key={section.heading}>
                      <a
                        href={`#section-${index + 1}`}
                        className="flex gap-2 text-sm text-foreground-600 hover:text-primary-700 transition-colors cursor-pointer"
                      >
                        <span className="text-primary-500 font-medium">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <span className="leading-snug">{section.heading}</span>
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>

              <div className="rounded-lg border border-background-200 bg-background-100 p-6">
                <h2 className="font-heading text-base font-bold text-foreground-950 mb-4">
                  同分类推荐
                </h2>
                <div className="flex flex-col gap-3">
                  {related.map((item) => (
                    <PostCard key={item.slug} post={item} variant="compact" />
                  ))}
                </div>
              </div>
            </aside>
          </div>

          <section className="mt-14 pt-10 border-t border-background-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                继续浏览相关话题
              </h2>
              <Link
                to={`/category/${category?.slug ?? ''}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-foreground-800 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                进入{category?.name}合集
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {related.map((item) => (
                <PostCard key={item.slug} post={item} variant="default" />
              ))}
            </div>
          </section>
        </div>
      </article>
    </SiteLayout>
  );
}